import { supabase } from './supabase';
import type {
  Category,
  Product,
  Option,
  OptionValue,
  ProductOptionLink,
} from './database.types';
import { visibleCategoriesSorted, productsForCategory } from './menuManager';
import {
  BOISSON_MENU_SLUG,
  buildMenuDrinkValues,
  placeMenuDrinkAfterTrigger,
  type DrinkValue,
} from './menuDrinks';

/**
 * Accès données du menu (catégories + produits + options façon Uber) depuis Supabase.
 * S'appuie sur la logique pure testée de menuManager.ts (tri, visibilité).
 * Modèle d'options : cf. MODELE_MENU_UBER.md (groupes réutilisables, sous-options, first_free).
 */

/** Valeur affichable d'un groupe d'options (= un « modificateur » Uber). */
export interface MenuOptionValue {
  /** id base (présent quand chargé depuis Supabase) — utilisé par l'éditeur. */
  id?: string;
  label: string;
  priceDeltaCents: number;
  isDefault: boolean;
}

/** Groupe d'options prêt pour l'UI + la logique de validation (productOptions.ts). */
export interface MenuOption {
  /** id base du groupe (présent quand chargé) — utilisé par l'éditeur. */
  id?: string;
  /** id de la liaison product_options (présent quand chargé) — pour réordonner/détacher. */
  linkId?: string;
  slug: string;
  name: string;
  type: 'single' | 'multi';
  required: boolean;
  min: number;
  max: number | null;
  /** 'flat' = supplément par valeur ; 'first_free' = 1ʳᵉ offerte puis extraEachCents. */
  pricingMode: 'flat' | 'first_free';
  extraEachCents: number;
  /** Sous-option : ce groupe ne s'affiche que si `dependsOn.value` est choisie dans `dependsOn.optionSlug`. */
  dependsOn: { optionSlug: string; value: string } | null;
  values: MenuOptionValue[];
}

export interface Menu {
  categories: Category[];
  productsByCategory: Record<string, Product[]>;
  /** Groupes d'options par article (id produit → groupes ordonnés). */
  optionsByProduct: Record<string, MenuOption[]>;
}

/** Convertit une ligne `options` (DB) + ses valeurs en MenuOption (UI). */
function toMenuOption(o: Option, values: MenuOptionValue[], linkId?: string): MenuOption {
  return {
    id: o.id,
    linkId,
    slug: o.slug,
    name: o.name,
    type: o.selection === 'multiple' ? 'multi' : 'single',
    required: !!o.is_required,
    min: o.min_choices ?? 0,
    max: o.max_choices ?? null,
    pricingMode: o.pricing_mode === 'first_free' ? 'first_free' : 'flat',
    extraEachCents: o.extra_each_cents ?? 0,
    // La dépendance existe dès qu'une valeur déclencheuse est définie ; le groupe
    // parent est optionnel (vide = « n'importe quel groupe » → cas boisson au menu).
    dependsOn: o.depends_on_value
      ? { optionSlug: o.depends_on_option_slug ?? '', value: o.depends_on_value }
      : null,
    values,
  };
}

/** Détecte un id base (UUID) — pour ignorer les refs legacy non résolvables dans options_json. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Charge les groupes d'options de chaque article (options → option_values).
 *
 * Règle d'or « la base fait foi » + ordre admin (apprentissages v15-v20) :
 *  - Si le produit a un `options_json` (tableau d'ids d'options) → c'est le CHOIX EXPLICITE
 *    de l'admin : on respecte SON ORDRE, on ignore les refs non-UUID (artefacts legacy).
 *  - Sinon → on retombe sur la table de liaison `product_options` (triée par position).
 * Les valeurs d'option viennent toujours de `option_values` (jamais de valeurs en dur).
 */
async function loadOptionsForProducts(
  products: { id: string; options_json?: unknown }[],
  drinkValues: DrinkValue[] = [],
): Promise<Record<string, MenuOption[]>> {
  const result: Record<string, MenuOption[]> = {};
  if (products.length === 0) return result;
  const productIds = products.map((p) => p.id);

  const { data: links } = await supabase
    .from('product_options')
    .select('*')
    .in('product_id', productIds);
  const linkList = (links as unknown as ProductOptionLink[] | null) ?? [];
  const linksByProduct: Record<string, ProductOptionLink[]> = {};
  for (const l of linkList) (linksByProduct[l.product_id] ??= []).push(l);

  // Détermine la liste ordonnée d'ids d'options par produit (options_json prioritaire, sinon M2M).
  const orderedIdsByProduct: Record<string, string[]> = {};
  const allOptionIds = new Set<string>();
  for (const p of products) {
    const json = (p as { options_json?: unknown }).options_json;
    let ids: string[] = [];
    if (Array.isArray(json) && json.length > 0) {
      ids = (json as unknown[]).filter((x): x is string => typeof x === 'string' && UUID_RE.test(x));
    }
    // Repli M2M si pas d'options_json exploitable.
    if (ids.length === 0) {
      ids = (linksByProduct[p.id] ?? [])
        .slice()
        .sort((a, b) => a.position - b.position)
        .map((l) => l.option_id);
    }
    // Dédoublonnage par id en préservant l'ordre.
    const seen = new Set<string>();
    ids = ids.filter((id) => (seen.has(id) ? false : (seen.add(id), true)));
    orderedIdsByProduct[p.id] = ids;
    ids.forEach((id) => allOptionIds.add(id));
  }
  if (allOptionIds.size === 0) return result;

  const optionIds = [...allOptionIds];
  const [{ data: opts }, { data: vals }] = await Promise.all([
    supabase.from('options').select('*').in('id', optionIds),
    supabase.from('option_values').select('*').in('option_id', optionIds),
  ]);

  const optionById = new Map<string, Option>(
    ((opts as unknown as Option[] | null) ?? []).map((o) => [o.id, o]),
  );
  const valuesByOption: Record<string, OptionValue[]> = {};
  for (const v of (vals as unknown as OptionValue[] | null) ?? []) {
    (valuesByOption[v.option_id] ??= []).push(v);
  }
  // id de liaison product_options (si présent) — pour l'éditeur admin (réordonner/détacher).
  const linkIdByProductOption: Record<string, string> = {};
  for (const l of linkList) linkIdByProductOption[`${l.product_id}:${l.option_id}`] = l.id;

  for (const p of products) {
    const groups = (orderedIdsByProduct[p.id] ?? [])
      .map((optId) => {
        const opt = optionById.get(optId);
        if (!opt) return null;
        // Groupe « boisson au menu » : les valeurs viennent AUTOMATIQUEMENT de la
        // catégorie Boissons du magasin (jamais des valeurs saisies). Sans boisson
        // disponible → on masque le groupe (rien à inclure).
        if (opt.slug === BOISSON_MENU_SLUG) {
          if (drinkValues.length === 0) return null;
          const values: MenuOptionValue[] = drinkValues.map((d) => ({
            label: d.label,
            priceDeltaCents: d.priceDeltaCents,
            isDefault: d.isDefault,
          }));
          return toMenuOption(opt, values, linkIdByProductOption[`${p.id}:${opt.id}`]);
        }
        const values: MenuOptionValue[] = (valuesByOption[opt.id] ?? [])
          .filter((v) => v.is_available)
          .sort((a, b) => a.position - b.position)
          .map((v) => ({
            id: v.id,
            label: v.name,
            priceDeltaCents: v.price_delta_cents ?? 0,
            isDefault: !!v.is_default,
          }));
        return toMenuOption(opt, values, linkIdByProductOption[`${p.id}:${opt.id}`]);
      })
      .filter((g): g is MenuOption => g !== null);
    // Boisson au menu affichée juste après son option déclencheuse (pas en fin de liste).
    if (groups.length) result[p.id] = placeMenuDrinkAfterTrigger(groups);
  }
  return result;
}

export async function loadMenu(tenantId: string): Promise<Menu> {
  const [{ data: cats }, { data: prods }] = await Promise.all([
    supabase.from('categories').select('*').eq('tenant_id', tenantId),
    supabase.from('products').select('*').eq('tenant_id', tenantId),
  ]);

  const allCategories = (cats as unknown as Category[]) ?? [];
  const categories = visibleCategoriesSorted(allCategories);
  const allProducts = (prods as unknown as Product[]) ?? [];
  const productsByCategory: Record<string, Product[]> = {};
  for (const c of categories) {
    productsByCategory[c.id] = productsForCategory(allProducts, c.id);
  }

  // Boissons du magasin (toutes catégories de boissons, produits dispo) → alimentent
  // automatiquement le sous-choix « boisson au menu ». Calculé une fois pour tous les produits.
  const drinkValues = buildMenuDrinkValues(allCategories, allProducts);
  const optionsByProduct = await loadOptionsForProducts(allProducts, drinkValues);

  return { categories, productsByCategory, optionsByProduct };
}
